import SearchPagination from '@react/search/components/SearchPagination';
import Footer from '@react/shared/components/Footer';
import Image from '@react/shared/components/Image';
import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import cx from 'classnames';
import { object } from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { isBrowser } from 'sly/config';
import { assetPath } from 'sly/lib/path';
import { useHistory, useLocation, useParams } from 'sly/lib/react-router';
import ArticlePreview from 'sly/resourceCenter/components/ArticlePreview';
import Header from 'sly/resourceCenter/components/Header';
import Helmet from 'sly/resourceCenter/components/Helmet';
import ResourceCenterChatBotWrapper from 'sly/resourceCenter/components/ResourceCenterChatBotWrapper';
import ReviewedByBlock from 'sly/resourceCenter/components/ReviewedByBlock';
import SubscribeEmail from 'sly/resourceCenter/components/SubscribeEmail';
import {
  ARTICLES_RANGE_FOR_PAGINATION,
  getPaginationProperties,
  getSearchItem,
} from 'sly/resourceCenter/helpers';
import { usePrefetch } from 'sly/services/api/prefetch';
import { pageNames, useEvents } from 'sly/services/events';
import { Link } from 'sly/system';

const Topic = ({ topicParams, articleParams }) => {
  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.Topic, {});
  }, []);

  const location = useLocation();
  const params = useParams();
  const { push } = useHistory();
  const { topic: topicSlug } = params;
  const { search, pathname } = location;

  const pageNumber = useMemo(
    () => getSearchItem(search, 'page-number') || 0,
    [location]
  );

  const {
    requestInfo: { result: topicRes, hasFinished: requestByTopicHasFinished },
  } = usePrefetch('getTopic', topicParams);
  const {
    requestInfo: { result: articleRes, hasFinished: articleRequestHasFinished },
  } = usePrefetch('getArticle', articleParams);

  if (
    !topicRes?.data?.[0] &&
    requestByTopicHasFinished &&
    articleRequestHasFinished
  ) {
    if (isBrowser) {
      push(RESOURCE_CENTER_PATH);
    }
  }

  if (!requestByTopicHasFinished) {
    return (
      <div
        className='flex h-[100vh] items-center justify-center'
        data-tp-id='gen-div-ee8c55cc-cd20-4e65-9369-7b47626713ad'
      >
        <Image
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          alt='loader'
          layout='fill'
          data-tp-id='gen-Image-57352f63-27f5-4326-9e8c-b9a905fa5f59'
        />
      </div>
    );
  }
  const { slug, name, description, img, reviewer } = topicRes?.data?.[0] || {};
  const { data: articles, meta } = articleRes || {};

  const articlesCount = meta?.pagination?.total;

  return (
    <>
      <ResourceCenterChatBotWrapper>
        <Helmet
          path={`${RESOURCE_CENTER_PATH}/${slug}`}
          title={`Resources on ${name}`}
          shortDescription={description}
        />

        <Header />

        <div
          className='relative w-full bg-black'
          data-tp-id='gen-div-5ac5af23-1dc2-40b4-bbbd-203534ad3e92'
        >
          <div
            className={cx(
              'relative z-[1] px-4 pt-12 pb-10 text-white',
              'mx-auto md:w-[42.5rem] md:py-20',
              'lg:w-[64.5rem]'
            )}
            data-tp-id='gen-div-91ef1106-9946-4113-a587-5e124e939937'
          >
            <Link
              to={RESOURCE_CENTER_PATH}
              className='font-b-m !text-white'
              data-tp-id='gen-Link-eaf2b089-407a-44d0-9ce0-a34973cf1883'
            >
              Resource Center Home
            </Link>
            <h1
              className='font-t-xxl mb-l mt-m text-white'
              data-tp-id='gen-div-0e6676bc-3579-430a-aafb-17876c1f2ff0'
            >
              {name}
            </h1>
            <div
              className='font-b-l'
              data-tp-id='gen-div-62282a3f-ba3a-47f5-8fb6-eedf21d06339'
            >
              {description}
            </div>
          </div>
          <Image
            objectFit='cover'
            className='absolute top-0 left-0 opacity-80'
            sources={[320, 425, 728, 1080, 1200]}
            sizes='100vw'
            path={img?.path}
            alt='test'
            data-tp-id='gen-Image-d023ad48-c07b-4984-af0f-cfb9e55115d3'
          />
        </div>

        {articlesCount && (
          <div
            className={cx(
              'mx-auto mt-l mb-xxl grid w-max gap-m',
              'md:grid-cols-[20.5rem_20.5rem] md:gap-l',
              'lg:grid-cols-[20.5rem_20.5rem_20.5rem]'
            )}
            data-tp-id='gen-div-af8cede1-1ed5-4da7-93a7-6e176af8c0ba'
          >
            {articles?.map(
              ({
                title,
                shortDescription,
                mainImg,
                slug,
                mainTopic,
                tagsList,
                id,
              }) => (
                <div
                  className='w-[20.5rem]'
                  key={id}
                  data-tp-id='gen-div-bfb2f092-386c-4b38-a700-0e55ea7476e3'
                >
                  <ArticlePreview
                    {...{
                      alternativeText: mainImg?.alternativeText,
                      title,
                      shortDescription,
                      path: mainImg?.path,
                      topic: mainTopic,
                      tagsList,
                      to: `${RESOURCE_CENTER_PATH}/${mainTopic.slug}/${slug}`,
                      key: id,
                    }}
                  />
                </div>
              )
            )}
          </div>
        )}

        {!articlesCount && (
          <div
            className='font-b-l mx-m mt-xl mb-xxl md:mx-auto md:w-[42.5rem] lg:w-[64.5rem]'
            data-tp-id='gen-div-73ac4171-8769-4d4e-ab30-dd3475f07a33'
          >
            {articleRequestHasFinished ? (
              'There are no articles found by this request'
            ) : (
              <div className='flex justify-center'>
                <Image
                  src={assetPath('uploads/react-assets/homebase/loader.svg')}
                  alt='loader'
                  layout='intrinsic'
                  height={200}
                  width={200}
                  data-tp-id='gen-Image-57352f63-27f5-4326-9e8c-b9a905fa5f59'
                />
              </div>
            )}
          </div>
        )}

        {articlesCount && (
          <div
            className='mx-auto mb-xxl w-max'
            data-tp-id='gen-div-a44852fa-e515-49a6-9420-965631b4c350'
          >
            {articlesCount > ARTICLES_RANGE_FOR_PAGINATION && (
              <SearchPagination
                pagination={getPaginationProperties({
                  basePath: `${RESOURCE_CENTER_PATH}/${topicSlug}`,
                  count: articlesCount,
                  current: pageNumber,
                })}
              />
            )}
          </div>
        )}
        {reviewer && (
          <div className='my-xxl mx-m md:mx-auto md:w-[42.5rem] lg:w-[64.5rem]'>
            <ReviewedByBlock reviewer={reviewer} />
          </div>
        )}

        <SubscribeEmail />

        <Footer />
      </ResourceCenterChatBotWrapper>
    </>
  );
};

Topic.propTypes = {
  match: object,
  location: object,
  history: object,
};

export default Topic;
